import { useState, useEffect, useRef, useCallback } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { motion } from 'framer-motion';
import { useProcessPDF, ProcessOptions } from '../hooks/useProcessPDF';
import { PipelineProgressCard } from './common/PipelineProgressCard';
import {
  XCircleIcon,
  DocumentPlusIcon,
  ArrowDownTrayIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  CheckCircleIcon,
  PresentationChartLineIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { DarkModeOptions } from '@/hooks/useDarkMode';
import { SplitOptions } from '@/hooks/useSplitPDF';
import { RotateOptions } from '@/hooks/useRotatePDF';
import { ExtractOptions } from '@/hooks/useExtractPages';
import { ImageToPdfOptions } from '@/hooks/useImagesToPdf';
import { CompressOptions } from '@/hooks/useCompressPDF';
import type { Tool } from '../types';
import { ContextualReportTrigger } from './common/ContextualReportTrigger';

type SelectedFileItem = {
  id: string;
  file: File;
};

export interface PDFProcessorProps {
  onComplete: (result: any) => void;
  onError: (error: Error) => void;
  onSelectionChange?: (files: File[]) => void;
  allowMultipleFiles: boolean;
  toolId: string | number;
  processActionName?: string;
  controls?: React.ReactNode;
  controlsLabel?: string;
  trustLabel?: string;
  autoProcess?: boolean;
  autoProcessDeps?: unknown[];
  autoProcessDebounceMs?: number;
  autoProcessOnSelect?: boolean;
  darkModePreviewOptions?: DarkModeOptions;
  splitPdfOptions?: SplitOptions;
  rotateOptions?: RotateOptions;
  extractOptions?: ExtractOptions;
  imageToPdfOptions?: ImageToPdfOptions;
  compressOptions?: CompressOptions;
  activeTool?: Tool | null;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function PDFProcessor({
  onComplete,
  onError,
  onSelectionChange,
  allowMultipleFiles,
  toolId,
  activeTool,
  controls,
  processActionName,
  darkModePreviewOptions,
  splitPdfOptions,
  rotateOptions,
  extractOptions,
  imageToPdfOptions,
  compressOptions,
}: PDFProcessorProps) {
  const [selectedFiles, setSelectedFiles] = useState<SelectedFileItem[]>([]);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState<string>('Initializing processing engine...');
  const [processStartTime, setProcessStartTime] = useState<number | undefined>(undefined);
  const { processDocument } = useProcessPDF();
  const [isProcessing, setIsProcessing] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [outputQuality, setOutputQuality] = useState<'low' | 'medium' | 'high'>('medium');
  const [pageRange, setPageRange] = useState<string>('');
  const [imageDimming, setImageDimming] = useState<number>(0.0);
  const [resultStats, setResultStats] = useState<{ originalSize?: number; processedSize?: number } | null>(null);
  const downloadUrlRef = useRef<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const addMoreInputRef = useRef<HTMLInputElement>(null);
  const idCounterRef = useRef(0);

  const isImageTool = activeTool?.name === 'Images to PDF';
  const isMergeTool = activeTool?.name === 'Merge PDFs';
  const isCompressTool =
    String(activeTool?.id || toolId || '').toLowerCase().includes('compress') ||
    String(activeTool?.id || toolId || '').toLowerCase().includes('optimize') ||
    (activeTool?.name || '').toLowerCase().includes('compress') ||
    (activeTool?.name || '').toLowerCase().includes('optimize');
  const isDarkModeTool =
    String(activeTool?.id || toolId || '').toLowerCase().includes('dark') ||
    (activeTool?.name || '').toLowerCase().includes('dark') ||
    (processActionName || '').toLowerCase().includes('dark');
  const acceptedFileExtensions = isImageTool ? '.png,.jpg,.jpeg,.webp' : '.pdf';

  useEffect(() => {
    downloadUrlRef.current = downloadUrl;
  }, [downloadUrl]);

  useEffect(() => {
    return () => {
      if (downloadUrlRef.current) {
        URL.revokeObjectURL(downloadUrlRef.current);
        downloadUrlRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    setSelectedFiles([]);
    setProgress(0);
    if (downloadUrlRef.current) {
      URL.revokeObjectURL(downloadUrlRef.current);
      downloadUrlRef.current = null;
    }
    setIsProcessing(false);
    setDownloadUrl(null);
    setResultStats(null);
  }, [toolId]);

  useEffect(() => {
    onSelectionChange?.(selectedFiles.map((s) => s.file));
  }, [onSelectionChange, selectedFiles]);

  const isProcessDisabled = useCallback(() => {
    if (isProcessing) return true;
    if (selectedFiles.length === 0) return true;
    if (isMergeTool && selectedFiles.length < 2) return true;
    if (activeTool?.name === 'Split PDF' && !splitPdfOptions) return true;
    if (activeTool?.name === 'Rotate PDF' && !rotateOptions) return true;
    if (activeTool?.name === 'Extract Pages' && !extractOptions) return true;
    return false;
  }, [activeTool?.name, extractOptions, isMergeTool, isProcessing, rotateOptions, selectedFiles.length, splitPdfOptions]);

  const handleProcessClick = useCallback(async () => {
    if (selectedFiles.length === 0) {
      onError(new Error('No files selected.'));
      return;
    }
    if (isMergeTool && selectedFiles.length < 2) {
      onError(new Error('Please select at least 2 PDF files to merge.'));
      return;
    }

    setIsProcessing(true);
    try {
      setProgress(0);
      setProgressMessage('Analyzing document structure...');
      setProcessStartTime(Date.now());
      if (downloadUrl) URL.revokeObjectURL(downloadUrl);
      setDownloadUrl(null);

      const processOptions: ProcessOptions = {
        activeToolName: activeTool?.name,
        darkModeOptions:
          isDarkModeTool
            ? {
                ...darkModePreviewOptions,
                outputQuality,
                pageRange: pageRange.trim() || undefined,
                imageDimming,
              }
            : undefined,
        splitPdfOptions: activeTool?.name === 'Split PDF' ? splitPdfOptions : undefined,
        rotateOptions: activeTool?.name === 'Rotate PDF' ? rotateOptions : undefined,
        extractOptions: activeTool?.name === 'Extract Pages' ? extractOptions : undefined,
        imageToPdfOptions: activeTool?.name === 'Images to PDF' ? imageToPdfOptions : undefined,
        compressOptions: activeTool?.name === 'Optimize PDF' ? compressOptions : undefined,
      };

      const handleProgressUpdate = (p: number, msg?: string) => {
        setProgress(Math.round(p * 100));
        if (msg) setProgressMessage(msg);
      };

      let result;
      if ((isMergeTool || isImageTool) && allowMultipleFiles) {
        result = await processDocument(
          selectedFiles.map((s) => s.file),
          handleProgressUpdate,
          processOptions
        );
      } else if (selectedFiles.length > 0) {
        const fileToProcess = selectedFiles[0].file;
        result = await processDocument(
          fileToProcess,
          handleProgressUpdate,
          processOptions
        );
      } else {
        onError(new Error('No file selected or invalid state for processing.'));
        return;
      }

      if (result && result.processedPdf) {
        const blob = new Blob([result.processedPdf as any], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        setDownloadUrl(url);
        setResultStats({
          originalSize: result.originalSize,
          processedSize: result.processedSize,
        });
      }
      if (result) {
        onComplete({
          ...result,
          appliedOptions: processOptions,
        });
      }
    } catch (error) {
      const err = error as Error;
      const msg = err.message || '';
      if (/encrypt|password|cipher/i.test(msg)) {
        onError(new Error('This PDF is password-protected or encrypted. Please remove the password before processing.'));
      } else {
        onError(err);
      }
    } finally {
      setIsProcessing(false);
    }
  }, [
    activeTool?.name,
    allowMultipleFiles,
    darkModePreviewOptions,
    downloadUrl,
    extractOptions,
    imageDimming,
    imageToPdfOptions,
    isDarkModeTool,
    isImageTool,
    isMergeTool,
    onComplete,
    onError,
    outputQuality,
    pageRange,
    processDocument,
    rotateOptions,
    selectedFiles,
    splitPdfOptions,
    compressOptions,
  ]);

  const handleFilesSelected = (files: FileList | null) => {
    if (files && files.length > 0) {
      const validFiles: File[] = [];
      const invalidFiles: string[] = [];
      const emptyFiles: string[] = [];

      Array.from(files).forEach((file) => {
        if (file.size === 0) {
          emptyFiles.push(file.name);
          return;
        }

        if (isImageTool) {
          const isValidType =
            file.type.startsWith('image/') ||
            /\.(jpg|jpeg|png|webp|gif|bmp|tiff)$/i.test(file.name);
          if (isValidType) {
            validFiles.push(file);
          } else {
            invalidFiles.push(file.name);
          }
        } else {
          const isValidType =
            file.type === 'application/pdf' ||
            file.name.toLowerCase().endsWith('.pdf');
          if (isValidType) {
            validFiles.push(file);
          } else {
            invalidFiles.push(file.name);
          }
        }
      });

      if (invalidFiles.length > 0) {
        const expected = isImageTool ? 'image files (JPG, PNG, WEBP)' : 'PDF files (.pdf)';
        onError(
          new Error(`Invalid file format: ${invalidFiles.join(', ')}. Please upload valid ${expected}.`)
        );
      }

      if (emptyFiles.length > 0) {
        onError(
          new Error(`Empty file detected (0 bytes): ${emptyFiles.join(', ')}. Please select a valid document.`)
        );
      }

      if (validFiles.length > 0) {
        const newFiles = validFiles.map((file) => {
          const nextCounter = idCounterRef.current++;
          const id = `${file.name}:${file.lastModified}:${file.size}:${nextCounter}`;
          return { id, file };
        });
        if (allowMultipleFiles) {
          setSelectedFiles((prev) => [...prev, ...newFiles]);
        } else {
          setSelectedFiles(newFiles.length > 0 ? [newFiles[0]] : []);
        }
        if (downloadUrl) URL.revokeObjectURL(downloadUrl);
        // Only trigger preview update by resetting output states; do not auto-process
        setIsProcessing(false);
        setDownloadUrl(null);
        setResultStats(null);
        setProgress(0);
      }
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleFilesSelected(event.target.files);
    if (event.target) event.target.value = '';
  };

  const clearAllFiles = () => {
    setSelectedFiles([]);
    setIsProcessing(false);
    setProgress(0);
    if (downloadUrl) URL.revokeObjectURL(downloadUrl);
    setDownloadUrl(null);
    setResultStats(null);
  };

  const removeFile = (id: string) => {
    setSelectedFiles((prev) => prev.filter((item) => item.id !== id));
    setIsProcessing(false);
    if (downloadUrl) URL.revokeObjectURL(downloadUrl);
    setDownloadUrl(null);
    setResultStats(null);
  };

  const moveFile = (index: number, direction: 'up' | 'down') => {
    setSelectedFiles((prev) => {
      const next = [...prev];
      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      if (targetIndex < 0 || targetIndex >= next.length) return prev;
      const [moved] = next.splice(index, 1);
      next.splice(targetIndex, 0, moved);
      return next;
    });
    setIsProcessing(false);
    if (downloadUrl) URL.revokeObjectURL(downloadUrl);
    setDownloadUrl(null);
    setResultStats(null);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
  };
  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    handleFilesSelected(event.dataTransfer.files);
  };

  const defaultActionName = () => {
    if (processActionName) return processActionName;
    switch (activeTool?.name) {
      case 'Merge PDFs':
        return 'Merge PDFs';
      case 'Split PDF':
        return 'Split PDF';
      case 'Rotate PDF':
        return 'Rotate PDF';
      case 'Extract Pages':
        return 'Extract Pages';
      case 'Optimize PDF':
        return 'Optimize PDF';
      case 'Dark Mode PDF':
      case 'Dark Mode':
        return 'Apply Dark Mode';
      case 'Images to PDF':
        return 'Convert to PDF';
      case 'Cleanse Metadata':
        return 'Cleanse Metadata';
      default:
        return 'Process PDF';
    }
  };

  const primaryActionLabel = () => {
    if (isDarkModeTool) {
      return 'Apply Dark Mode & Generate PDF';
    }
    if (processActionName) {
      return processActionName;
    }
    return defaultActionName();
  };

  const downloadFileName = () => {
    const firstFile = selectedFiles[0]?.file;
    if (!firstFile) {
      const slugName = (activeTool?.name || 'processed')
        .toLowerCase()
        .replace(/\s+/g, '-');
      return `${slugName}-result.pdf`;
    }

    const baseName = firstFile.name.replace(/\.[^/.]+$/, '');
    const toolName = activeTool?.name || '';

    if (toolName === 'Dark Mode PDF' || toolName === 'Dark Mode') {
      return `${baseName}_DarkMode.pdf`;
    } else if (toolName === 'Merge PDFs') {
      return `${baseName}_Merged.pdf`;
    } else if (toolName === 'Split PDF') {
      return `${baseName}_Split.pdf`;
    } else if (toolName === 'Rotate PDF') {
      return `${baseName}_Rotated.pdf`;
    } else if (toolName === 'Extract Pages') {
      return `${baseName}_Extracted.pdf`;
    } else if (toolName === 'Optimize PDF') {
      return `${baseName}_Optimized.pdf`;
    } else if (toolName === 'Cleanse Metadata' || toolName === 'Clean Metadata') {
      return `${baseName}_Cleaned.pdf`;
    } else if (toolName === 'Images to PDF') {
      return `${baseName}_Converted.pdf`;
    }

    return `${baseName}_Processed.pdf`;
  };

  return (
    <div role="region" aria-label="PDF processing workspace" className="space-y-5 relative">
      {/* 1. Uploader Drop Zone */}
      <div className="space-y-3">
        {selectedFiles.length === 0 ? (
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`group flex flex-col items-center justify-center p-8 sm:p-10 border-2 border-dashed rounded-2xl cursor-pointer transition-all duration-200 ease-in-out text-center ${
              isDragging
                ? 'border-cyan-400 bg-cyan-500/10 shadow-lg shadow-cyan-500/10'
                : 'border-slate-800 hover:border-slate-700 bg-slate-900/60 hover:bg-slate-900/90'
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept={acceptedFileExtensions}
              multiple={allowMultipleFiles}
              onChange={handleFileChange}
              className="hidden"
              id={`pdf-upload-${toolId}`}
            />
            <div
              className={`w-12 h-12 rounded-xl mb-3 flex items-center justify-center transition-colors ${
                isDragging
                  ? 'bg-cyan-500/20 text-cyan-300'
                  : 'bg-slate-800/80 text-cyan-400 group-hover:bg-cyan-500 group-hover:text-slate-950'
              }`}
            >
              <DocumentPlusIcon className="w-6 h-6 transition-transform group-hover:scale-110" />
            </div>
            <p className="text-sm font-bold text-slate-100 group-hover:text-white">
              {isImageTool
                ? 'Drop images here or choose files from your device'
                : 'Drop PDF files here or choose files from your device'}
            </p>
            <p className="text-xs text-slate-400 mt-1.5">
              {isImageTool
                ? 'Supports PNG, JPG, JPEG, WebP format'
                : allowMultipleFiles
                ? 'Select multiple PDF files to organize and merge'
                : 'Select a PDF document to process'}
            </p>
          </div>
        ) : (
          /* 2. Selected File Workspace */
          <div className="space-y-3">
            <div className="flex items-center justify-between pb-1 border-b border-slate-800/80">
              <span className="text-xs font-semibold text-slate-300">
                Selected {isImageTool ? 'Images' : 'Files'} ({selectedFiles.length})
              </span>
              <div className="flex items-center gap-2">
                {allowMultipleFiles && (
                  <>
                    <input
                      ref={addMoreInputRef}
                      type="file"
                      accept={acceptedFileExtensions}
                      multiple
                      onChange={handleFileChange}
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => addMoreInputRef.current?.click()}
                      className="text-xs text-cyan-400 hover:text-cyan-300 font-semibold flex items-center gap-1 transition-colors"
                    >
                      <DocumentPlusIcon className="w-3.5 h-3.5" />
                      Add More
                    </button>
                  </>
                )}
                {selectedFiles.length > 1 && (
                  <button
                    type="button"
                    onClick={clearAllFiles}
                    className="text-xs text-slate-500 hover:text-rose-400 transition-colors"
                  >
                    Clear All
                  </button>
                )}
              </div>
            </div>

            <ul className="space-y-2 max-h-72 overflow-y-auto pr-0.5">
              {selectedFiles.map((item, index) => (
                <li
                  key={item.id}
                  className="group flex items-center justify-between gap-2.5 p-2.5 rounded-xl bg-slate-900/80 border border-slate-800 hover:border-slate-700 transition-all"
                >
                  <div className="flex items-center gap-2.5 min-w-0 flex-1">
                    <div className="w-8 h-8 rounded-lg bg-slate-800 border border-slate-700/60 text-cyan-400 flex items-center justify-center shrink-0 font-bold text-[10px]">
                      {index + 1}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs sm:text-sm font-semibold text-slate-100 truncate">
                        {item.file.name}
                      </p>
                      <p className="text-[10px] text-slate-400">
                        {formatFileSize(item.file.size)}
                      </p>
                    </div>
                  </div>

                  {/* Controls: Reorder buttons for multi-file tools */}
                  <div className="flex items-center gap-1 shrink-0">
                    {allowMultipleFiles && selectedFiles.length > 1 && (
                      <div className="flex items-center gap-0.5 bg-slate-800/80 rounded-lg p-0.5 border border-slate-700/50">
                        <button
                          type="button"
                          onClick={() => moveFile(index, 'up')}
                          disabled={index === 0}
                          title="Move Up"
                          className="p-1 text-slate-400 hover:text-cyan-300 disabled:opacity-30 disabled:hover:text-slate-400 transition-colors"
                        >
                          <ChevronUpIcon className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => moveFile(index, 'down')}
                          disabled={index === selectedFiles.length - 1}
                          title="Move Down"
                          className="p-1 text-slate-400 hover:text-cyan-300 disabled:opacity-30 disabled:hover:text-slate-400 transition-colors"
                        >
                          <ChevronDownIcon className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={() => removeFile(item.id)}
                      title="Remove File"
                      className="p-1 text-slate-400 hover:text-rose-400 transition-colors"
                    >
                      <XCircleIcon className="w-4 h-4" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>

            {/* Hint for Merge PDFs */}
            {isMergeTool && selectedFiles.length === 1 && (
              <p className="text-xs text-amber-400/90 bg-amber-500/10 border border-amber-500/20 px-3 py-2 rounded-lg text-center">
                Add at least 1 more PDF file to perform a merge.
              </p>
            )}
          </div>
        )}
      </div>

      {/* 2. Tool Specific Controls */}
      {selectedFiles.length > 0 && (controls || isDarkModeTool) && (
        <div className="py-2 border-t border-slate-800/80 space-y-3">
          {controls}

          {/* User-facing Output Quality selector, Page Range, and Image Dimming for Dark Mode */}
          {isDarkModeTool && (
            <div className="p-3.5 rounded-xl bg-slate-900/60 border border-slate-800 space-y-3.5">
              {/* 1. Page Range Selection */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <label htmlFor="darkmode-page-range" className="block text-xs font-bold text-slate-200 uppercase tracking-wider">
                    Pages to Process
                  </label>
                  <span className="text-[10px] text-slate-400">Leave blank for all</span>
                </div>
                <input
                  id="darkmode-page-range"
                  type="text"
                  value={pageRange}
                  onChange={(e) => setPageRange(e.target.value)}
                  placeholder="e.g., 1-5, 8, 11 (Leave blank for all)"
                  disabled={isProcessing}
                  className="w-full px-3 py-2 text-xs bg-slate-950/80 border border-slate-800 rounded-xl text-slate-200 placeholder-slate-500 focus:outline-none focus:border-cyan-500/70 focus:ring-1 focus:ring-cyan-500/40 disabled:opacity-50 transition-colors"
                />
                <p className="text-[10px] text-slate-400 leading-snug">
                  Note: Preview shows all pages. Only the downloaded PDF will be cropped to your selection.
                </p>
              </div>

              {/* 2. Output Quality Selector */}
              <div className="space-y-1.5 pt-2 border-t border-slate-800/60">
                <div className="flex items-center justify-between">
                  <label className="block text-xs font-bold text-slate-200 uppercase tracking-wider">
                    Output Quality
                  </label>
                  <span className="text-[11px] text-cyan-400 font-medium">
                    {outputQuality === 'low'
                      ? '1.0x scale • 60% quality'
                      : outputQuality === 'medium'
                      ? '1.5x scale • 85% quality'
                      : '2.0x scale • 95% quality'}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    {
                      value: 'low',
                      label: 'Low',
                      badge: 'Small File',
                    },
                    {
                      value: 'medium',
                      label: 'Medium',
                      badge: 'Balanced',
                    },
                    {
                      value: 'high',
                      label: 'High',
                      badge: 'Sharpest',
                    },
                  ].map((item) => {
                    const isActive = outputQuality === item.value;
                    return (
                      <button
                        key={item.value}
                        type="button"
                        onClick={() => setOutputQuality(item.value as 'low' | 'medium' | 'high')}
                        disabled={isProcessing}
                        className={`relative flex flex-col items-center justify-center py-2 px-1.5 rounded-xl border text-center transition-all cursor-pointer ${
                          isActive
                            ? 'border-cyan-500/80 bg-cyan-500/10 text-cyan-300 ring-2 ring-cyan-500/30 font-bold shadow-sm'
                            : 'border-slate-800 bg-slate-950/70 text-slate-300 hover:text-white hover:border-slate-700'
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                        aria-pressed={isActive}
                        aria-label={`Output Quality: ${item.label} (${item.badge})`}
                      >
                        <span className="text-xs">{item.label}</span>
                        <span className="text-[10px] text-slate-400 mt-0.5 font-normal">
                          ({item.badge})
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* 3. Image Dimming Slider */}
              <div className="space-y-1.5 pt-2 border-t border-slate-800/60">
                <div className="flex items-center justify-between">
                  <label htmlFor="darkmode-image-dimming" className="block text-xs font-bold text-slate-200 uppercase tracking-wider">
                    Image Dimming
                  </label>
                  <span className="text-[11px] font-semibold text-cyan-400">
                    {Math.round(imageDimming * 100)}%
                  </span>
                </div>
                <input
                  id="darkmode-image-dimming"
                  type="range"
                  min="0"
                  max="0.5"
                  step="0.05"
                  value={imageDimming}
                  onChange={(e) => setImageDimming(parseFloat(e.target.value))}
                  disabled={isProcessing}
                  className="w-full h-1.5 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-cyan-400 focus:outline-none disabled:opacity-50"
                />
                <div className="flex items-center justify-between text-[10px] text-slate-400">
                  <span>0% (Untouched)</span>
                  <span>50% (Max dimming)</span>
                </div>
              </div>

              {/* Preview Approximation Disclaimer */}
              <div className="pt-2 border-t border-slate-800/60">
                <p className="text-[10px] text-slate-500 italic leading-snug">
                  Note: Preview uses CSS filters for speed. The final downloaded PDF is generated with a higher-fidelity algorithm that may look slightly different.
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 3. Prominent Primary CTA Button (Visible above the fold, above the download/status section) */}
      <div className="pt-2">
        <button
          type="button"
          onClick={handleProcessClick}
          disabled={selectedFiles.length === 0 || isProcessing || isProcessDisabled()}
          className="w-full py-3.5 px-5 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 disabled:from-slate-800 disabled:to-slate-800 disabled:text-slate-500 disabled:border-slate-800 disabled:shadow-none text-slate-950 font-bold rounded-xl transition-all shadow-lg shadow-cyan-500/25 text-sm disabled:cursor-not-allowed flex items-center justify-center gap-2 tracking-wide cursor-pointer focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:ring-offset-2 focus:ring-offset-slate-950"
        >
          <span>{primaryActionLabel()}</span>
        </button>
      </div>

      {/* 4. Result, Download, or Instructional State Area */}
      <div className="pt-2 border-t border-slate-800/80 space-y-3">
        {/* If isProcessing is true: Show loading pipeline */}
        {isProcessing ? (
          <PipelineProgressCard
            progress={progress}
            progressMsg={progressMessage}
            toolName={activeTool?.name || 'PDF Engine'}
            startTime={processStartTime}
          />
        ) : !isProcessing && downloadUrl ? (
          /* If isProcessing is false AND downloadUrl exists: Show "PDF Ready for Download" card */
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            className="p-4 rounded-2xl bg-cyan-950/30 border border-cyan-500/40 space-y-3 text-center"
          >
            <div className="flex items-center justify-center gap-2 text-cyan-400 font-bold text-sm">
              <CheckCircleIcon className="w-5 h-5 text-cyan-400" />
              <span>PDF Ready for Download</span>
            </div>

            {resultStats?.originalSize && resultStats?.processedSize && (
              <div className="p-3 rounded-xl bg-slate-900/90 border border-slate-800 text-left space-y-2 shadow-sm">
                <div className="flex items-center justify-between text-xs font-bold text-white">
                  <span className="flex items-center gap-1.5">
                    <PresentationChartLineIcon className="w-4 h-4 text-cyan-400 shrink-0" />
                    <span>
                      {isCompressTool ? 'File Size Comparison' : 'File Processing Summary'}
                    </span>
                  </span>
                  {(() => {
                    const savedBytes = resultStats.originalSize - resultStats.processedSize;
                    if (savedBytes > 0) {
                      const savedPct = (savedBytes / resultStats.originalSize) * 100;
                      if (savedPct >= 1) {
                        return (
                          <span className="text-[10px] uppercase tracking-wider font-extrabold px-2.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                            Saved {Math.round(savedPct)}% ({formatFileSize(savedBytes)})
                          </span>
                        );
                      }
                      return (
                        <span className="text-[10px] uppercase tracking-wider font-bold px-2.5 py-0.5 rounded-full bg-cyan-500/20 text-cyan-300 border border-cyan-500/30">
                          Minimal Reduction
                        </span>
                      );
                    }
                    if (isCompressTool) {
                      return (
                        <span className="text-[10px] uppercase tracking-wider font-bold px-2.5 py-0.5 rounded-full bg-slate-800 text-slate-300 border border-slate-700">
                          Already Optimized
                        </span>
                      );
                    }
                    return (
                      <span className="text-[10px] uppercase tracking-wider font-bold px-2.5 py-0.5 rounded-full bg-cyan-500/20 text-cyan-300 border border-cyan-500/30">
                        Transformed
                      </span>
                    );
                  })()}
                </div>
                <div className="flex items-center justify-between text-xs text-slate-300">
                  <span>Original: <span className="font-semibold text-slate-200">{formatFileSize(resultStats.originalSize)}</span></span>
                  <span>
                    {isCompressTool ? 'Optimized:' : 'Processed:'}{' '}
                    <span className="font-bold text-cyan-400">{formatFileSize(resultStats.processedSize)}</span>
                  </span>
                </div>
                {resultStats.originalSize <= resultStats.processedSize && (
                  <div className="text-[10px] text-slate-400 border-t border-slate-800/80 pt-1.5 leading-snug">
                    {isDarkModeTool
                      ? `File size adjusted (+${formatFileSize(resultStats.processedSize - resultStats.originalSize)}) to embed high-clarity dark theme canvas layers.`
                      : isCompressTool
                      ? 'This PDF is already efficiently compressed.'
                      : 'Document successfully transformed and regenerated.'}
                  </div>
                )}
              </div>
            )}

            <a
              href={downloadUrl}
              download={downloadFileName()}
              onClick={(e) => { if (isProcessing) e.preventDefault(); }}
              className={`flex items-center justify-center gap-2 w-full py-3 px-4 bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold rounded-xl transition-colors shadow-lg shadow-cyan-500/20 text-sm cursor-pointer ${
                isProcessing ? 'pointer-events-none opacity-50 cursor-not-allowed' : ''
              }`}
              aria-disabled={isProcessing}
            >
              <ArrowDownTrayIcon className="w-4 h-4 stroke-[2.5]" />
              <span>Download {isMergeTool ? 'Merged PDF' : 'Processed PDF'}</span>
            </a>

            <button
              type="button"
              onClick={clearAllFiles}
              disabled={isProcessing}
              className="w-full py-2 px-3 bg-slate-900 hover:bg-slate-800 text-slate-300 hover:text-white text-xs font-semibold rounded-xl border border-slate-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isMergeTool ? 'Start Another Merge' : 'Process Another File'}
            </button>

            <ContextualReportTrigger
              toolName={activeTool?.name || 'PDF Engine'}
              fileSizeMb={
                selectedFiles[0]?.file
                  ? (selectedFiles[0].file.size / (1024 * 1024)).toFixed(2) + ' MB'
                  : undefined
              }
              operationStatus="success"
              customLabel="Something wrong with the result output?"
              className="pt-1"
            />
          </motion.div>
        ) : !isProcessing && downloadUrl === null && selectedFiles.length > 0 ? (
          /* If isProcessing is false AND downloadUrl is null AND a file is selected: Show instructional empty state card */
          <div className="p-4 sm:p-5 rounded-2xl bg-cyan-950/20 border border-cyan-500/25 text-center space-y-2.5">
            <div className="w-9 h-9 mx-auto rounded-full bg-cyan-500/15 border border-cyan-500/30 flex items-center justify-center text-cyan-400">
              <InformationCircleIcon className="w-5 h-5" />
            </div>
            <p className="text-xs sm:text-sm font-semibold text-slate-200 leading-relaxed max-w-sm mx-auto">
              Ready to process. Adjust your settings below, then click 'Apply Dark Mode' to generate the download.
            </p>
            <p className="text-[11px] text-slate-400 leading-normal">
              Review live visual changes in the canvas preview on the right before generating your final file.
            </p>
          </div>
        ) : (
          /* If no file is selected: Show the existing "No document loaded" state */
          <div className="p-4 rounded-2xl border border-dashed border-slate-800 bg-slate-950/40 text-center">
            <p className="text-xs font-medium text-slate-500">No document loaded</p>
          </div>
        )}
      </div>

      {/* 5. Short Factual Privacy Note */}
      <p className="text-[11px] text-slate-400 text-center leading-normal">
        Files are processed locally in your browser.
      </p>
    </div>
  );
}

function ErrorFallback({ error, resetErrorBoundary }: any) {
  return (
    <div role="alert" className="p-4 bg-rose-950/40 text-white rounded-xl border border-rose-500/40 space-y-3">
      <h3 className="text-sm font-bold text-rose-300">Processing Error</h3>
      <p className="text-xs text-rose-200/90">{error.message}</p>
      <div className="flex items-center justify-between gap-3 pt-1">
        <button
          onClick={resetErrorBoundary}
          className="px-3.5 py-1.5 bg-slate-900 text-xs font-semibold text-white rounded-lg border border-slate-700 hover:bg-slate-800"
        >
          Try Again
        </button>
        <ContextualReportTrigger
          toolName="PDF Engine"
          lastError={error.message}
          operationStatus="failed"
          variant="button"
          customLabel="Report error"
        />
      </div>
    </div>
  );
}

export default function PDFProcessorWithErrorBoundary(props: PDFProcessorProps) {
  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      resetKeys={[
        props.activeTool?.id || props.toolId,
        props.splitPdfOptions?.startPage,
        props.splitPdfOptions?.endPage,
      ]}
    >
      <PDFProcessor {...props} />
    </ErrorBoundary>
  );
}
